import { Link } from '@tanstack/react-router';
import {
  type ColumnDef,
  type ColumnFiltersState,
  createColumnHelper,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import {
  Banknote,
  CalendarDays,
  ChevronDown,
  CircleArrowLeft,
  CloudUpload,
  Columns3,
  Menu,
  Pencil,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  type EnrollmentRequestItem,
  isAlreadyEnrolledNoop,
  type PreviewChargeBreakdown,
  usePreviewEnrollment,
} from '~entities/enrollment/api/preview-enrollment';
import {
  getStartEnrollmentOutcome,
  useStartEnrollment,
} from '~entities/enrollment/api/start-enrollment';
import { useGetEvent } from '~entities/events';
import { useGetActiveStudentsByEventId } from '~entities/events/api/get-enrolled-active-students-by-event-id';
import { useAddStudentToEntity } from '~entities/stripe/api/add-student-to-class';
import { useGetStudentFullDetailsById } from '~entities/student/api/get-student-full-details-by-id';
import type { EventStudent } from '~entities/student/modal/event-student';
import type { StudentSearch } from '~entities/student/modal/student-search';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { AgeRangeWarningDialog } from '~features/classes/ui/age-range-warning-dialog';
import { EditAttendanceDatesDialog } from '~features/events/ui/edit-attendance-dates-dialog';
import { StudentSelector } from '~features/students/ui/student-selector';

import { useGetCurrency } from '~shared/api/currency-api';
import { AttendanceExportDropdown } from '~shared/components/attendance-export-dropdown';
import { DropEventDialog } from '~shared/components/dialog/drop-event-dialog';
import { EnrollioTable } from '~shared/components/enrollio-table';
import { PaymentConfirmationDialog } from '~shared/components/payment-confirmation-dialog';
import { DataTableColumnHeader } from '~shared/components/table/header';
import {
  getAttendanceOptions,
  getStudentSelectedDatesForEvent,
  shouldAllowAttendanceDateSelection,
  toAttendanceDateKey,
} from '~shared/helpers/attendance-dates';
import {
  ENROLLED_STUDENTS_CSV_HEADERS,
  exportToCSV,
} from '~shared/helpers/csv';
import {
  calculateAgeFromISO,
  formatAgeString,
  formatDateOnlySafe,
} from '~shared/helpers/datetime';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';
import { Input } from '~shared/shadcn/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~shared/shadcn/ui/popover';
import { Separator } from '~shared/shadcn/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '~shared/shadcn/ui/sheet';
import { isAgeInRange } from '~shared/utils/date-time';

const DEFAULT_MIN_AGE_YEARS = 3;
const DEFAULT_MIN_AGE_MONTHS = 0;
const DEFAULT_MAX_AGE_YEARS = 80;
const DEFAULT_MAX_AGE_MONTHS = 0;

const eventStatus = (
  student: EventStudent,
  eventId: string,
): string | undefined => {
  const match = student.events?.find((event) => {
    const id =
      typeof event.eventId === 'string'
        ? event.eventId
        : event.eventId?._id;
    return id === eventId;
  });
  return match?.subscriptionStatus;
};

// Create a column helper
const columnHelper = createColumnHelper<EventStudent>();

interface BuildColsOptions {
  eventId: string;
  attendanceOptions: { key: string; isoDate: string; label: string }[];
  allowAttendanceDates: boolean;
  hasAdminAccess: boolean;
  onEditAttendanceDates: (student: EventStudent) => void;
}

const buildCols = ({
  eventId,
  attendanceOptions,
  allowAttendanceDates,
  hasAdminAccess,
  onEditAttendanceDates,
}: BuildColsOptions): ColumnDef<EventStudent>[] => [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <div className='px-4'>
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => {
            table.toggleAllPageRowsSelected(!!value);
          }}
          aria-label='Select all'
        />
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className='px-4'>
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => {
              row.toggleSelected(!!value);
            }}
            aria-label='Select row'
          />
        </div>
      );
    },
  }),
  columnHelper.accessor('name', {
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='Student Name'
      />
    ),
    cell: (info) => (
      <Button variant={'link'} className='text-custom-secondary-1'>
        <Link
          to={`/students/$studentId/details`}
          params={{
            studentId: info.row.original._id,
          }}
        >
          {info.getValue()}
        </Link>
      </Button>
    ),
  }),
  columnHelper.accessor(
    (row) => eventStatus(row, eventId) ?? 'active',
    {
      id: 'status',
      meta: { mobileLabel: 'Status' },
      header: (info) => (
        <DataTableColumnHeader
          className='pl-4 text-black'
          column={info.column}
          title='Status'
        />
      ),
      cell: (info) => {
        const status = info.getValue();
        const isInactive =
          status === 'term_ended' ||
          status === 'ended' ||
          status === 'inactive' ||
          status === 'dropped' ||
          status === 'cancelled';
        return (
          <div className='px-4'>
            {isInactive ? (
              <Badge variant='secondary' className='text-xs'>
                Inactive
              </Badge>
            ) : (
              <Badge variant='default' className='text-xs'>
                Active
              </Badge>
            )}
          </div>
        );
      },
    },
  ),
  columnHelper.accessor('familyName', {
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='Family Name'
      />
    ),
    cell: (info) => <div className='px-4'> {info.getValue()}</div>,
  }),
  columnHelper.accessor('gender', {
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='Gender'
      />
    ),
    cell: (info) => <div className='px-4'> {info.getValue()}</div>,
  }),
  columnHelper.accessor('dob', {
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='DOB'
      />
    ),
    cell: (info) => {
      const dob = info.getValue();
      if (!dob) return <div className='px-4'>-</div>;
      return (
        <div className='px-4'>
          {formatDateOnlySafe(dob, 'dd-MM-yyyy', 'Invalid date')}
        </div>
      );
    },
  }),
  columnHelper.accessor('dob', {
    id: 'age',
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='Age'
      />
    ),
    cell: (info) => {
      const { years, months } = calculateAgeFromISO(info.getValue());
      return (
        <div className='px-4'>
          {`${years} ${years > 1 ? 'years' : 'year'} ${months} ${months > 1 ? 'months' : 'month'}`}
        </div>
      );
    },
  }),
  columnHelper.accessor('tShirtSize', {
    id: 'balance',
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='Balance'
      />
    ),
    cell: () => <div className='px-4'>0</div>,
  }),
  columnHelper.accessor('tShirtSize', {
    id: 'costumeSize',
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='Costume Size'
      />
    ),
    cell: (info) => <div className='px-4'> {info.getValue()}</div>,
  }),
  ...(allowAttendanceDates
    ? [
        columnHelper.display({
          id: 'attendanceDates',
          header: (info) => (
            <DataTableColumnHeader
              className='pl-4 text-black'
              column={info.column}
              title='Attendance Dates'
            />
          ),
          cell: ({ row }) => {
            const student = row.original;
            const selectedDates = getStudentSelectedDatesForEvent(
              student,
              eventId,
            );
            const total = attendanceOptions.length;

            const isFullAttendance = selectedDates.length === 0;
            const selectedKeys = isFullAttendance
              ? new Set(attendanceOptions.map((o) => o.key))
              : new Set(selectedDates.map((d) => toAttendanceDateKey(d)));
            const count = selectedKeys.size;

            const dateList = attendanceOptions.filter((o) =>
              selectedKeys.has(o.key),
            );

            return (
              <div className='flex items-center gap-2 px-4'>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type='button'
                      className='flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground'
                    >
                      <CalendarDays className='size-4 shrink-0' />
                      <span>
                        {count === total ? (
                          <Badge variant='secondary' className='text-xs'>
                            All {total}
                          </Badge>
                        ) : (
                          <Badge variant='outline' className='text-xs'>
                            {count} / {total}
                          </Badge>
                        )}
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className='w-60 p-3' align='start'>
                    <p className='mb-2 text-xs font-medium text-muted-foreground'>
                      Selected attendance dates
                    </p>
                    <ul className='space-y-1'>
                      {dateList.map((opt) => (
                        <li key={opt.key} className='text-sm'>
                          {opt.label}
                        </li>
                      ))}
                    </ul>
                  </PopoverContent>
                </Popover>
                {hasAdminAccess && (
                  <Button
                    variant='ghost'
                    size='sm'
                    className='h-6 px-1.5 text-xs'
                    onClick={() => onEditAttendanceDates(student)}
                  >
                    <Pencil className='size-3' />
                  </Button>
                )}
              </div>
            );
          },
        }),
      ]
    : []),
];

interface EventEnrollmentsPage {
  eventId: string;
}

export const EventEnrollmentsPage = ({ eventId }: EventEnrollmentsPage) => {
  const {
    data: eventData,
    isFetching: isEventFetching,
    isError: isEventError,
  } = useGetEvent({ eventId });
  const { data, isFetching } = useGetActiveStudentsByEventId({ eventId });

  const { hasAdminAccess } = useCurrentUserRole();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(
    null,
  );
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [showDropDialog, setShowDropDialog] = useState(false);
  const [studentsToDropFromEvent, setStudentsToDropFromEvent] = useState<
    Array<{ studentId: string; studioId: string }>
  >([]);
  const [pendingStudent, setPendingStudent] = useState<StudentSearch | null>(
    null,
  );
  const [ageRangeWarningOpen, setAgeRangeWarningOpen] = useState(false);
  const [editAttendanceStudent, setEditAttendanceStudent] =
    useState<EventStudent | null>(null);
  const [eventActionsOpen, setEventActionsOpen] = useState(false);
  const [showV2PreviewDialog, setShowV2PreviewDialog] = useState(false);
  const [enrollmentPreview, setEnrollmentPreview] = useState<{
    amountDueTotal: number;
    charges: PreviewChargeBreakdown[];
  } | null>(null);
  const [pendingV2Requests, setPendingV2Requests] = useState<
    EnrollmentRequestItem[] | null
  >(null);

  const attendanceOptions = eventData ? getAttendanceOptions(eventData) : [];
  const allowAttendanceDates = eventData
    ? shouldAllowAttendanceDateSelection(eventData)
    : false;

  const cols = buildCols({
    eventId,
    attendanceOptions,
    allowAttendanceDates,
    hasAdminAccess,
    onEditAttendanceDates: (student) => setEditAttendanceStudent(student),
  });

  const { data: selectedStudent } = useGetStudentFullDetailsById(
    { studentId: selectedStudentId ?? '' },
    { enabled: !!selectedStudentId },
  );
  const { data: studioDetails } = useGetStudioDetails();
  const { data: currencyData } = useGetCurrency();
  const currencySymbol = currencyData?.symbol ?? '$';
  const isV2Enrollment = studioDetails?.isV2 === true;
  const addStudentToEntity = useAddStudentToEntity();
  const previewEnrollmentMutation = usePreviewEnrollment();
  const startEnrollmentMutation = useStartEnrollment();

  if (isEventFetching || isFetching) {
    return <div>Loading...</div>;
  }

  if (isEventError || !eventData || eventData._id !== eventId) {
    return (
      <div className='space-y-2 p-6'>
        <p className='text-lg font-semibold'>Event not found</p>
        <p className='text-muted-foreground'>
          This event may have been removed or is no longer available. Refresh
          the events list and try again.
        </p>
      </div>
    );
  }

  if (!data) {
    return <div>No data found</div>;
  }

  const proceedToPayment = (studentId: string) => {
    const availableSeats = eventData?.remainingSeats ?? 0;
    const isEventFull =
      eventData?.maxSize && eventData.maxSize > 0 ? availableSeats <= 0 : false;

    if (isEventFull && !hasAdminAccess) {
      toast.error(
        'Event is full – no spots available. Please try another event.',
      );
      setSelectedStudentId(null);
      return;
    }

    setSelectedStudentId(studentId);
    setIsPaymentDialogOpen(true);
  };

  const onStudentSelectionChange = (
    value: StudentSearch | undefined | null,
  ) => {
    if (!value) {
      setSelectedStudentId(null);
      return;
    }

    const studentAge = calculateAgeFromISO(value.dob);
    const isInRange = isAgeInRange({
      age: { years: studentAge.years, months: studentAge.months },
      startAge: {
        years: eventData?.startYear ?? DEFAULT_MIN_AGE_YEARS,
        months: eventData?.startMonth ?? DEFAULT_MIN_AGE_MONTHS,
      },
      endAge: {
        years: eventData?.endYear ?? DEFAULT_MAX_AGE_YEARS,
        months: eventData?.endMonth ?? DEFAULT_MAX_AGE_MONTHS,
      },
    });

    setSelectedStudentId(value._id.toString());

    if (!isInRange) {
      setPendingStudent(value);
      setAgeRangeWarningOpen(true);
      return;
    }

    proceedToPayment(value._id.toString());
  };

  const handleAgeRangeWarningConfirm = () => {
    if (pendingStudent) {
      proceedToPayment(pendingStudent._id.toString());
      setPendingStudent(null);
    }
    setAgeRangeWarningOpen(false);
  };

  const handleAgeRangeWarningClose = (open: boolean) => {
    setAgeRangeWarningOpen(open);
    if (!open) {
      setPendingStudent(null);
    }
  };

  const handlePaymentConfirm = async (
    paymentMethod: 'card' | 'us_bank_account' | 'au_becs_debit' | null,
    skipPaymentMethod = false,
    paymentMethodId: string | null = null,
  ) => {
    if (!selectedStudent || !eventData) {
      throw new Error('Event or student details are not available');
    }

    if (!isV2Enrollment) {
      return addStudentToEntity.mutateAsync({
        studioId: selectedStudent.studioId,
        entityId: eventId,
        studentIds: [selectedStudent._id],
        entityType: 'event',
        paymentMethod: skipPaymentMethod ? null : paymentMethod,
        skipPaymentMethod,
        allowCapacityOverride: hasAdminAccess,
      });
    }

    if (!skipPaymentMethod && !paymentMethodId) {
      toast.error('Please select a saved payment method');
      return null;
    }

    const studioId = selectedStudent.studioId || studioDetails?._id;
    if (!studioId) {
      toast.error('Studio not found for enrollment');
      return null;
    }

    // Match class enroll: V2 skip and charge both go through preview/start.
    const resolvedPaymentMethodId = skipPaymentMethod
      ? undefined
      : (paymentMethodId ?? undefined);
    const sessionId = eventData.session?._id;
    const requests: EnrollmentRequestItem[] = [
      {
        entityId: eventId,
        entityType: 'event',
        sessionId,
        skipPaymentMethod,
        paymentMethodId: resolvedPaymentMethodId,
        allowCapacityOverride: hasAdminAccess,
        students: [
          {
            studentId: selectedStudent._id,
            ...(resolvedPaymentMethodId
              ? { paymentMethodId: resolvedPaymentMethodId }
              : {}),
          },
        ],
      },
    ];

    const preview = await previewEnrollmentMutation.mutateAsync({
      studioId,
      source: 'studio_portal',
      mode: 'manual',
      requests,
    });

    if (isAlreadyEnrolledNoop(preview)) {
      toast.warning(
        `Student already enrolled: ${preview.existingEnrollmentsList.join(', ')}`,
      );
      return {
        status: 200,
        message: 'Student already enrolled',
      };
    }

    setPendingV2Requests(requests);
    setEnrollmentPreview({
      amountDueTotal: preview.amountDueTotal ?? 0,
      charges: preview.charges ?? [],
    });
    setIsPaymentDialogOpen(false);
    setShowV2PreviewDialog(true);

    // Keep PaymentConfirmationDialog open until we hand off — return a
    // success-shaped response so it closes cleanly without treating this as failure.
    return {
      status: 200,
      message: 'Preview ready',
    };
  };

  const handleV2PreviewConfirm = async () => {
    if (!selectedStudent || !pendingV2Requests?.length) {
      toast.error('Enrollment preview expired. Please try again.');
      setShowV2PreviewDialog(false);
      return;
    }

    const studioId = selectedStudent.studioId || studioDetails?._id;
    if (!studioId) {
      toast.error('Studio not found for enrollment');
      return;
    }

    try {
      const startResult = await startEnrollmentMutation.mutateAsync({
        studioId,
        source: 'studio_portal',
        mode: 'manual',
        requests: pendingV2Requests,
      });
      const outcome = getStartEnrollmentOutcome(startResult);
      if (outcome.type !== 'success') {
        return;
      }
      setShowV2PreviewDialog(false);
      setEnrollmentPreview(null);
      setPendingV2Requests(null);
      setSelectedStudentId(null);
    } catch {
      // Error toast handled by useStartEnrollment.onError
    }
  };

  const handleV2PreviewDialogClose = (open: boolean) => {
    setShowV2PreviewDialog(open);
    if (!open) {
      setEnrollmentPreview(null);
      setPendingV2Requests(null);
    }
  };

  const handleDropEvent = () => {
    const selectedIds = Object.keys(rowSelection);
    if (selectedIds.length === 0 || !data) return;

    const students = selectedIds
      .map((id) => {
        const index = parseInt(id);
        const student = data[index];
        if (!student?._id || !student?.studioId) return null;
        return { studentId: student._id, studioId: student.studioId };
      })
      .filter((s): s is { studentId: string; studioId: string } => s !== null);

    if (students.length === 0) return;
    setStudentsToDropFromEvent(students);
    setShowDropDialog(true);
  };

  const handleExport = () => {
    if (!data || data.length === 0) {
      console.error('No data available to export.');
      return;
    }

    const headers = Object.values(ENROLLED_STUDENTS_CSV_HEADERS);

    const formattedData = data.map((student) => {
      const { years, months } = calculateAgeFromISO(student.dob);
      const formattedDate = student.dob
        ? formatDateOnlySafe(student.dob, 'dd-MM-yyyy', '')
        : '';

      return {
        [ENROLLED_STUDENTS_CSV_HEADERS.STUDENT_NAME]: student.name,
        [ENROLLED_STUDENTS_CSV_HEADERS.FAMILY_NAME]: student.familyName || '',
        [ENROLLED_STUDENTS_CSV_HEADERS.GENDER]: student.gender,
        [ENROLLED_STUDENTS_CSV_HEADERS.DOB]: formattedDate,
        [ENROLLED_STUDENTS_CSV_HEADERS.AGE]: formatAgeString(years, months),
        [ENROLLED_STUDENTS_CSV_HEADERS.COSTUME_SIZE]: student.tShirtSize || '',
      };
    });

    const eventName = eventData?.title ?? 'event';
    exportToCSV(
      formattedData,
      headers,
      `${eventName}-enrolled-students-${format(new Date(), 'yyyy-MM-dd-HH-mm-ss')}.csv`,
    );
  };

  return (
    <div>
      <Link to='/events' className='flex items-center gap-2'>
        <CircleArrowLeft className='size-6' />
        <h2 className='text-2xl font-semibold'>Students Enrolled</h2>
      </Link>
      <div className='mb-10 mt-6'>
        <EnrollioTable
          ariaLabel='Students enrolled in event'
          totalLabel='Enrolled Students'
          columns={cols}
          data={data}
          sorting={sorting}
          onSortingChange={setSorting}
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          enableRowSelection
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          showSelectionCount
          filterHeader={(table) => {
            const closeEventActions = () => {
              setEventActionsOpen(false);
            };

            const renderStudentSelector = (fullWidth: boolean) =>
              hasAdminAccess ? (
                <StudentSelector
                  placeholder='Enroll Student in Event'
                  onChange={onStudentSelectionChange}
                  className={fullWidth ? 'w-full' : undefined}
                />
              ) : null;

            const renderDropEventButton = () =>
              hasAdminAccess && Object.keys(rowSelection).length > 0 ? (
                <Button variant='destructive' onClick={handleDropEvent}>
                  <Trash2 className='mr-2 size-4' />
                  Drop Event
                </Button>
              ) : null;

            const renderColumnsMenu = (stacked: boolean) => (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant='outline'
                    className={stacked ? 'w-full justify-start' : undefined}
                  >
                    <Columns3 />
                    Columns
                    <ChevronDown
                      className={stacked ? 'ml-auto size-4' : 'ml-2 size-4'}
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  {table
                    .getAllColumns()
                    .filter((column) => column.getCanHide())
                    .map((column) => (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className='capitalize'
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) => {
                          column.toggleVisibility(!!value);
                        }}
                      >
                        {column.id}
                      </DropdownMenuCheckboxItem>
                    ))}
                </DropdownMenuContent>
              </DropdownMenu>
            );

            const renderEventActions = (stacked: boolean) => {
              const buttonClassName = stacked
                ? 'w-full justify-start'
                : undefined;

              return (
                <>
                  {stacked ? renderStudentSelector(true) : null}

                  <Button
                    variant='outline'
                    className={buttonClassName}
                    onClick={() => {
                      closeEventActions();
                      handleExport();
                    }}
                    disabled={!data || data.length === 0}
                  >
                    <CloudUpload className='mr-2 size-4' />
                    Exports
                  </Button>

                  <Button variant='outline' className={buttonClassName} asChild>
                    <Link
                      to='/events/$eventId/payments'
                      params={{ eventId }}
                      onClick={closeEventActions}
                    >
                      <Banknote className='mr-2 size-4' />
                      Payments
                    </Link>
                  </Button>

                  <div
                    className={
                      stacked
                        ? 'w-full [&_button>svg:last-child]:ml-auto [&_button]:w-full [&_button]:justify-start'
                        : undefined
                    }
                  >
                    <AttendanceExportDropdown
                      entityId={eventId}
                      entityTitle={eventData?.title}
                    />
                  </div>

                  {renderColumnsMenu(stacked)}

                  {hasAdminAccess ? (
                    <Button
                      variant='outline'
                      className={buttonClassName}
                      asChild
                    >
                      <Link
                        to='/events/$eventId/update'
                        params={{ eventId }}
                        onClick={closeEventActions}
                      >
                        <Pencil />
                        Edit
                      </Link>
                    </Button>
                  ) : null}
                </>
              );
            };

            return (
              <div className='flex w-full min-w-0 flex-col items-stretch gap-4 py-4'>
                <div className='hidden w-full min-w-0 flex-wrap items-center justify-between gap-4 md:flex'>
                  <Input
                    placeholder='Search Students...'
                    onChange={(event) =>
                      table
                        .getColumn('name')
                        ?.setFilterValue(event.target.value)
                    }
                    className='max-w-sm'
                  />
                  <div className='flex flex-wrap items-center gap-4'>
                    {renderStudentSelector(false)}
                    {renderDropEventButton()}
                    {renderEventActions(false)}
                  </div>
                </div>

                <div className='flex w-full min-w-0 flex-col gap-3 md:hidden'>
                  <Sheet
                    open={eventActionsOpen}
                    onOpenChange={setEventActionsOpen}
                  >
                    <SheetTrigger asChild>
                      <Button variant='outline' className='w-full'>
                        <Menu className='mr-2 size-4' />
                        Event actions
                      </Button>
                    </SheetTrigger>
                    <SheetContent
                      side='right'
                      className='w-[300px] overflow-y-auto'
                    >
                      <SheetHeader>
                        <SheetTitle>Event actions</SheetTitle>
                        <SheetDescription>
                          Enroll a student, export lists, open payments, choose
                          columns, or edit this event.
                        </SheetDescription>
                      </SheetHeader>
                      <div className='mt-6 flex flex-col gap-3'>
                        {renderEventActions(true)}
                      </div>
                    </SheetContent>
                  </Sheet>
                  <Input
                    placeholder='Search Students...'
                    onChange={(event) =>
                      table
                        .getColumn('name')
                        ?.setFilterValue(event.target.value)
                    }
                    className='w-full'
                  />
                  {renderDropEventButton()}
                </div>
              </div>
            );
          }}
        />
      </div>

      {selectedStudent && (
        <PaymentConfirmationDialog
          isOpen={isPaymentDialogOpen}
          onClose={() => {
            setIsPaymentDialogOpen(false);
          }}
          onConfirm={handlePaymentConfirm}
          isLoading={
            addStudentToEntity.isPending || previewEnrollmentMutation.isPending
          }
          capacityInfo={
            eventData
              ? {
                  remainingSeats: eventData.remainingSeats ?? 0,
                  maxSize: eventData.maxSize ?? 0,
                  isFull:
                    eventData.maxSize && eventData.maxSize > 0
                      ? (eventData.remainingSeats ?? 0) <= 0
                      : false,
                }
              : undefined
          }
          isAdmin={hasAdminAccess}
          data={{
            student: {
              _id: selectedStudent._id,
              name: selectedStudent.name,
              parentId: {
                _id: selectedStudent.parentId._id,
                firstName: selectedStudent.parentId.firstName,
                lastName: selectedStudent.parentId.lastName,
                email: selectedStudent.parentId.email,
              },
              entities:
                selectedStudent.events?.map((event) => ({
                  eventId: {
                    _id: event.eventId?._id ?? '',
                    title: event.eventId?.title ?? '',
                  },
                  subscriptionStatus: event.subscriptionStatus,
                })) ?? [],
            },
            entity: {
              id: eventData?._id ?? '',
              title: eventData?.title ?? '',
              type: 'event',
              studioId: selectedStudent.studioId,
            },
            fees: [
              {
                label: 'Tuition Fee',
                amount: eventData?.tuitionFee ?? 0,
              },
              ...(eventData?.registrationFeeAmount
                ? [
                    {
                      label: 'Registration Fee',
                      amount: eventData.registrationFeeAmount,
                    },
                  ]
                : []),
              ...(eventData?.costumeFee
                ? [{ label: 'Costume Fee', amount: eventData.costumeFee }]
                : []),
            ],
          }}
        />
      )}

      <Dialog
        open={showV2PreviewDialog}
        onOpenChange={handleV2PreviewDialogClose}
      >
        <DialogContent className='sm:max-w-[520px]'>
          <DialogHeader>
            <DialogTitle>Confirm Enrollment Charges</DialogTitle>
          </DialogHeader>

          <div className='space-y-4'>
            {selectedStudent && (
              <div className='space-y-1 text-sm'>
                <p>
                  <span className='font-medium'>Student:</span>{' '}
                  {selectedStudent.name}
                </p>
                <p>
                  <span className='font-medium'>Parent:</span>{' '}
                  {selectedStudent.parentId.firstName}{' '}
                  {selectedStudent.parentId.lastName}
                </p>
                <p>
                  <span className='font-medium'>Event:</span> {eventData?.title}
                </p>
              </div>
            )}

            <Separator />

            <div className='space-y-3'>
              <p className='font-medium'>💰 Fees Breakdown:</p>
              {(enrollmentPreview?.charges ?? []).length === 0 ? (
                <p className='text-sm text-muted-foreground'>
                  No charges due (already enrolled or {currencySymbol}0).
                </p>
              ) : (
                enrollmentPreview?.charges.map((charge) => (
                  <div
                    key={charge.studentId}
                    className='space-y-2 rounded-md border p-3'
                  >
                    <div className='flex items-center justify-between font-medium'>
                      <span>{charge.studentName || selectedStudent?.name}</span>
                      <span>
                        {currencySymbol}
                        {charge.amountDueWithTransactionFee.toFixed(2)}
                      </span>
                    </div>
                    <div className='space-y-1 text-sm text-muted-foreground'>
                      {charge.lineItems
                        .filter(
                          (line) =>
                            line.code !== 'transaction_fee' &&
                            line.code !== 'wallet_credit',
                        )
                        .map((line, index) => (
                          <div
                            key={`${charge.studentId}-${line.code}-${index}`}
                            className='flex justify-between gap-4'
                          >
                            <span>{line.label}</span>
                            <span>
                              {currencySymbol}
                              {line.amount.toFixed(2)}
                            </span>
                          </div>
                        ))}
                      {charge.walletCreditApplied > 0 && (
                        <div className='flex justify-between gap-4 text-emerald-700'>
                          <span>Wallet credit</span>
                          <span>
                            -{currencySymbol}
                            {charge.walletCreditApplied.toFixed(2)}
                          </span>
                        </div>
                      )}
                      {charge.transactionFeeAmount > 0 && (
                        <div className='flex justify-between gap-4'>
                          <span>Transaction fee</span>
                          <span>
                            {currencySymbol}
                            {charge.transactionFeeAmount.toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className='flex items-center justify-between border-t pt-3 text-base font-semibold'>
              <span>Total due</span>
              <span>
                {currencySymbol}
                {(enrollmentPreview?.amountDueTotal ?? 0).toFixed(2)}
              </span>
            </div>
          </div>

          <DialogFooter className='gap-2 pt-2'>
            <Button
              variant='outline'
              onClick={() => handleV2PreviewDialogClose(false)}
              disabled={startEnrollmentMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleV2PreviewConfirm()}
              disabled={startEnrollmentMutation.isPending}
            >
              {startEnrollmentMutation.isPending
                ? 'Enrolling…'
                : 'Confirm & Enroll'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showDropDialog && studentsToDropFromEvent.length > 0 && (
        <DropEventDialog
          open={showDropDialog}
          onOpenChange={setShowDropDialog}
          eventId={eventId}
          students={studentsToDropFromEvent}
          onSuccess={() => {
            setShowDropDialog(false);
            setStudentsToDropFromEvent([]);
            setRowSelection({});
          }}
        />
      )}

      {pendingStudent && eventData && (
        <AgeRangeWarningDialog
          open={ageRangeWarningOpen}
          onOpenChange={handleAgeRangeWarningClose}
          onConfirm={handleAgeRangeWarningConfirm}
          studentName={pendingStudent.name}
          studentAge={calculateAgeFromISO(pendingStudent.dob)}
          classAgeRange={{
            startYear: eventData.startYear ?? DEFAULT_MIN_AGE_YEARS,
            startMonth: eventData.startMonth ?? DEFAULT_MIN_AGE_MONTHS,
            endYear: eventData.endYear ?? DEFAULT_MAX_AGE_YEARS,
            endMonth: eventData.endMonth ?? DEFAULT_MAX_AGE_MONTHS,
          }}
          entityType='event'
        />
      )}

      {editAttendanceStudent && eventData && (
        <EditAttendanceDatesDialog
          open={!!editAttendanceStudent}
          onOpenChange={(open) => {
            if (!open) setEditAttendanceStudent(null);
          }}
          studentId={editAttendanceStudent._id}
          studentName={editAttendanceStudent.name}
          eventId={eventId}
          eventData={eventData}
          currentSelectedDates={getStudentSelectedDatesForEvent(
            editAttendanceStudent,
            eventId,
          )}
        />
      )}
    </div>
  );
};
